import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const supabaseServiceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

if (!supabaseUrl) {
  throw new Error('Missing Supabase environment variable NEXT_PUBLIC_SUPABASE_URL. Please add it to your .env.local file.');
}

// ============================================================================
// Supabase Admin Client (Server-side only)
//
// The browser never talks to Supabase directly anymore (BFF migration): all
// data access goes through our `/api` routes using this service-role client,
// after `authenticateRequest` has validated the caller. There is no browser
// (anon-key) client left in this module.
// ============================================================================

/**
 * Get the Supabase admin client with service role key.
 * This client bypasses RLS and has full database access.
 * 
 * Creates a fresh client per call to avoid stale connections
 * in serverless environments (Vercel).
 * 
 * WARNING: Only use this on the server side (API routes).
 * Never expose the service role key to the client.
 * 
 * @throws Error if SUPABASE_SERVICE_ROLE_KEY is not set
 */
export function supabaseAdmin(): SupabaseClient<Database> {
  if (!supabaseServiceRoleKey) {
    throw new Error(
      'Missing SUPABASE_SERVICE_ROLE_KEY environment variable. ' +
      'This is required for admin operations. Add it to your .env.local file.'
    );
  }

  return createClient<Database>(supabaseUrl, supabaseServiceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  });
}

export type { Database };

